import Card from "@/components/Card"
import InputError from "@/components/InputError"
import SkeletonRow from "@/components/SkeletonRow"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { web } from "@/config/System"
import api from "@/lib/axios"
import { DataPrestasi, DataSiswa } from "@/types"
import { AxiosError } from "axios"
import { Loader2 } from "lucide-react"
import { useState } from "react"
import toast from "react-hot-toast"
import { FaCheck, FaFaceSmileBeam } from "react-icons/fa6"
import { GiBackwardTime } from "react-icons/gi"
import { MdBlockFlipped } from "react-icons/md"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { useSearchParams } from "react-router-dom"

type DataPrestasiLoket = {
  id_data_pendaftaran: string
  keterangan_loket_4_3: string
  status_loket_4_3: number | boolean
  siswa: DataSiswa
  prestasi: DataPrestasi[]
}

type ValidasiGlobalState = {
  id_data_pendaftaran: string
  status_loket_4_3: number
  keterangan_loket_4_3: string
}

const patchDataValidasiGlobal = async (form: ValidasiGlobalState) => {
  const response = await api.patch(
    "/verifikator/daftar-kembali/loket-4/data-prestasi/validasi-global",
    form
  )
  return response.data
}

const patchDataResetValidasi = async (id_data_pendaftaran: string) => {
  const response = await api.patch(
    "/verifikator/daftar-kembali/reset?loket=4&menu=3",
    { id_data_pendaftaran }
  )
  return response.data
}

export default function Prestasi({
  data,
  isPending,
  error,
}: {
  data: DataPrestasiLoket
  isPending: boolean
  error: AxiosError | null
}) {
  const [searchParams] = useSearchParams()
  const idParams = searchParams.get("id")

  const [form, setForm] = useState({
    keterangan_loket_4_3: "",
  })

  const queryClient = useQueryClient()
  const {
    mutate: mutateValidationGlobal,
    isPending: isPendingValidationGlobal,
    error: errorValidationGlobal,
  } = useMutation({
    mutationFn: patchDataValidasiGlobal,
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: ["dataPrestasi", idParams],
      })
      queryClient.invalidateQueries({
        queryKey: ["dataStatusLoket", idParams],
      })
      toast.success(data?.message || "Success.")
    },
    onError: (
      error: AxiosError<{ message?: string; errors?: Record<string, string[]> }>
    ) => {
      const err = error as AxiosError<{
        message?: string
        errors?: Record<string, string[]>
      }>
      const errorMessage =
        err.response?.data?.message || "An unexpected error occurred."
      toast.error(errorMessage)
    },
  })

  const {
    mutate: mutateResetValidation,
    isPending: isPendingResetValidation,
    // error: errorResetValidation,
  } = useMutation({
    mutationFn: patchDataResetValidasi,
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: ["dataPrestasi", idParams],
      })
      queryClient.invalidateQueries({
        queryKey: ["dataStatusLoket", idParams],
      })
      toast.success(data?.message || "Success.")
    },
    onError: (
      error: AxiosError<{ message?: string; errors?: Record<string, string[]> }>
    ) => {
      const err = error as AxiosError<{
        message?: string
        errors?: Record<string, string[]>
      }>
      const errorMessage =
        err.response?.data?.message || "An unexpected error occurred."
      toast.error(errorMessage)
    },
  })

  const handleValidasiGlobal = (status: number) => {
    mutateValidationGlobal({
      ...form,
      id_data_pendaftaran: data?.id_data_pendaftaran,
      status_loket_4_3: status,
    })
  }

  const handleReset = () => {
    mutateResetValidation(data?.id_data_pendaftaran || "")
  }

  if (isPending) {
    return (
      <div className="flex flex-col gap-y-3">
        {Array(10)
          .fill(null)
          .map((_, index) => {
            return <SkeletonRow key={index} />
          })}
      </div>
    )
  }

  if (error?.response?.status === 404) {
    return (
      <div>
        Data <span className="text-primary">{idParams}</span> tidak ditemukan.
      </div>
    )
  }

  if (!data) {
    return (
      <div className="flex justify-center items-center h-[50vh]">
        <div>
          <FaFaceSmileBeam className="mx-auto" size={40} />
          <h1 className="mt-3 text-xl font-semibold">
            Lakukan pencarian menggunakan nomor pendaftaran atau nama siswa.
          </h1>
        </div>
      </div>
    )
  }

  return (
    <div>
      {data?.prestasi?.length > 0 ? (
        <div className="flex flex-col gap-y-5">
          {data?.prestasi?.map((item, index) => {
            return (
              <Card key={item.id_data_prestasi}>
                <div className="flex-1 h-[70vh] relative">
                  <iframe
                    src={item.path_bukti_prestasi || ""}
                    title="Prestasi"
                    className="absolute h-full w-full"
                  />
                </div>
                <div className="mt-3 flex-1 overflow-auto">
                  <div className="h-10 w-10 rounded-full flex justify-center items-center bg-primary text-white">
                    {index + 1}
                  </div>
                  <table className="table-auto w-full border-collapse border rounded-xl overflow-hidden">
                    <tbody>
                      <tr>
                        <th className="py-2 px-4 border font-semibold text-start">
                          Nama Lengkap
                        </th>
                        <td className="py-2 px-4 border">:</td>
                        <td className="py-2 px-4 border">
                          {data?.siswa?.nama_lengkap}
                        </td>
                      </tr>
                      <tr>
                        <th className="py-2 px-4 border font-semibold text-start">
                          Jenis Prestasi
                        </th>
                        <td className="py-2 px-4 border">:</td>
                        <td className="py-2 px-4 border">
                          {item?.jenis_prestasi?.nama_jenis_prestasi}
                        </td>
                      </tr>
                      <tr>
                        <th className="py-2 px-4 border font-semibold text-start">
                          Jenjang Prestasi
                        </th>
                        <td className="py-2 px-4 border">:</td>
                        <td className="py-2 px-4 border">
                          {item?.jenjang_prestasi?.nama_jenjang_prestasi}
                        </td>
                      </tr>
                      <tr>
                        <th className="py-2 px-4 border font-semibold text-start">
                          Lembaga Penyelenggara
                        </th>
                        <td className="py-2 px-4 border">:</td>
                        <td className="py-2 px-4 border">
                          Himpunan Mahasiswa Jurusan Biologi dan Perikanan
                          Kelautan, Fakultas Matematika dan Ilmu Pengetahuan
                          Alam, {web.univ}
                        </td>
                      </tr>
                      <tr>
                        <th className="py-2 px-4 border font-semibold text-start">
                          Individu/Kelompok
                        </th>
                        <td className="py-2 px-4 border">:</td>
                        <td className="py-2 px-4 border">
                          {item?.grup_prestasi?.nama_grup_prestasi}
                        </td>
                      </tr>
                      <tr>
                        <th className="py-2 px-4 border font-semibold text-start">
                          Deskripsi
                        </th>
                        <td className="py-2 px-4 border">:</td>
                        <td className="py-2 px-4 border">{item?.deskripsi}</td>
                      </tr>
                      {/* <tr>
                        <th className="py-2 px-4 border font-semibold text-start">
                          Capaian Pretasi
                        </th>
                        <td className="py-2 px-4 border">:</td>
                        <td className="py-2 px-4 border">
                          {item?.capaian_prestasi?.nama_capaian_prestasi}
                        </td>
                      </tr> */}
                    </tbody>
                  </table>
                </div>
              </Card>
            )
          })}

          <Card className="mt-3 px-4">
            <table className="table-auto w-full border-collapse border rounded-xl overflow-hidden">
              <tbody>
                <tr>
                  <th className="py-2 px-4 border w-[45%] font-semibold text-start">
                    Status Validasi
                  </th>
                  <td className="py-2 px-4 border w-[5%]">:</td>
                  <td className="py-2 px-4 border w-[50%]">
                    {data?.status_loket_4_3 === 1 ? (
                      <Badge variant={"dark"}>Sudah Valid</Badge>
                    ) : data?.status_loket_4_3 === 0 ? (
                      <Badge variant={"outline"}>Tidak Valid</Badge>
                    ) : (
                      <Badge variant={"secondary"}>Belum Valid</Badge>
                    )}
                  </td>
                </tr>
                <tr>
                  <th className="py-2 px-4 border w-[45%] font-semibold text-start">
                    Catatan
                  </th>
                  <td className="py-2 px-4 border w-[5%]">:</td>
                  <td className="py-2 px-4 border w-[50%]">
                    {data?.keterangan_loket_4_3}
                  </td>
                </tr>
              </tbody>
            </table>

            <div className="mt-5 px-4">
              {data?.status_loket_4_3 === null ? (
                <>
                  <h1 className="font-semibold text-xl">
                    Kesimpulan Hasil Validasi
                  </h1>
                  <p className="text-muted-foreground">
                    Jika terlalu banyak data prestasi yang tidak sesuai maka
                    kesimpulan status validasi dapat dinyatakan tidak valid.
                  </p>
                  <div className="mt-3">
                    <Label>Input Catatan</Label>
                    <Textarea
                      placeholder="Ketik catatan..."
                      className="mt-1"
                      value={form.keterangan_loket_4_3}
                      onChange={(event) =>
                        setForm((prev) => ({
                          ...prev,
                          keterangan_loket_4_3: event.target.value,
                        }))
                      }
                    />
                    <InputError
                      text={errorValidationGlobal?.response?.data.errors?.keterangan_loket_4_3?.join(
                        ","
                      )}
                    />
                  </div>
                  <div className="mt-5 flex justify-end gap-3">
                    {isPendingValidationGlobal ? (
                      <Button
                        variant={"outline"}
                        className="flex items-center gap-3"
                        disabled
                      >
                        <>
                          <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                          Processing...
                        </>
                      </Button>
                    ) : (
                      <>
                        <Button
                          variant={"outline"}
                          className="flex items-center gap-3"
                          onClick={() => handleValidasiGlobal(0)}
                          disabled={isPendingValidationGlobal}
                        >
                          <MdBlockFlipped />
                          Tidak Valid
                        </Button>
                        <Button
                          className="flex items-center gap-3"
                          onClick={() => handleValidasiGlobal(1)}
                          disabled={isPendingValidationGlobal}
                        >
                          <FaCheck />
                          Valid
                        </Button>
                      </>
                    )}
                  </div>
                </>
              ) : (
                <div className="flex justify-end">
                  <Button
                    variant="outline"
                    className="flex items-center gap-3"
                    onClick={() => handleReset()}
                    disabled={isPendingResetValidation}
                  >
                    {isPendingResetValidation ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      <>
                        <GiBackwardTime />
                        Reset Hasil Validasi
                      </>
                    )}
                  </Button>
                </div>
              )}
            </div>
          </Card>
        </div>
      ) : (
        "Data pretasi tidak ditemukan."
      )}
    </div>
  )
}
